<template>
	<div class="dateTimePicker">
		<div :class="['flex items-center gap-03', { 'pointer-events-none': disabled }]">
			<DatePicker
				v-model="date"
				class="datePicker w-[9rem]"
				@open="isCalendarOpen = true"
				@close="isCalendarOpen = false"
				@update:model-value="notify"
			>
				<CalendarInput :date="dateTime" :placeholder="placeholder" :calendar-open="isCalendarOpen" />

				<template #footer>
					<slot name="calendarFooter" />
				</template>
			</DatePicker>

			<TimePicker
				:class="['timePicker', { 'timePicker--disabled': !disabled && disabledTimepicker }]"
				:hours="time.hour"
				:minutes="time.minute"
				:disabled-hours="disabledHours"
				:disabled-minutes="disabledMinutes"
				@change="onTimeChange"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { DatePicker, TimePicker, type Time } from '@bluebrick/saas-ui'
import { debounce } from 'lodash-es'
import { DateTime } from 'luxon'
import { toRefs, computed, ref, watch } from 'vue'
import CalendarInput from '@/common/components/CalendarInput.vue'
import { fromDateTime } from '@/common/dateUtils'

const props = withDefaults(
	defineProps<{
		debounceMs?: number
		disabled?: boolean
		disabledTimepicker?: boolean
		disabledHours?: boolean
		disabledMinutes?: boolean
		placeholder?: string
	}>(),
	{
		debounceMs: 250,
		placeholder: ''
	}
)

const modelValue = defineModel<DateTime>({ required: true })

const { debounceMs } = toRefs(props)

const isCalendarOpen = ref(false)

const date = ref(new Date(modelValue.value.year, modelValue.value.month - 1, modelValue.value.day))
const time = ref(fromDateTime(modelValue.value))

const dateTime = computed(() => DateTime.fromJSDate(date.value).set({ hour: time.value.hour, minute: time.value.minute }))

const notify = debounce(() => {
	if (dateTime.value.toISO() === modelValue.value.toISO()) {
		return
	}

	// Will `emit('update:modelValue', ...)`
	// This way of updating the modelValue assures that its time zone will be retained
	modelValue.value = modelValue.value.set({
		year: dateTime.value.year,
		month: dateTime.value.month,
		day: dateTime.value.day,
		hour: time.value.hour,
		minute: time.value.minute,
		second: time.value.second,
		millisecond: time.value.millisecond
	})
}, debounceMs.value)

function onTimeChange(newTime: Time) {
	if (time.value.isValid) {
		time.value = time.value.set({ hour: newTime.hours, minute: newTime.minutes })

		notify()
	}
}

watch(
	modelValue,
	() => {
		date.value = new Date(modelValue.value.year, modelValue.value.month - 1, modelValue.value.day)
		time.value = fromDateTime(modelValue.value)
	},
	{ immediate: true }
)
</script>

<style lang="scss" scoped>
.datePicker {
	.calendar,
	:deep(.calendar) {
		overflow-x: hidden;
		white-space: nowrap;
	}

	&--disabled {
		pointer-events: none;
		opacity: 0.5;
	}
}

.timePicker {
	:deep(.triggerInput input:read-only) {
		color: theme('colors.ink.60');
	}

	:deep(.inputWrapper.size--m) {
		height: 2.128rem;
	}

	:deep(.inputWrapper.is-readonly) {
		border: var(--border-disabled);
	}

	:deep(.timePickerRollers) {
		background-color: theme('colors.level.01');
	}

	&--disabled {
		pointer-events: none;
		opacity: 0.5;
	}
}
</style>
